import { ModalTypes } from "constants/navigation";
import queryString from "query-string";
import { getContextByModalType } from "selectors";
import { closeDispatchingModal, updateModalContext } from "actions";
import appHistory from "appHistory";
import { getStore } from "store";

const ManagedModalTypes: ModalTypes[] = [
  ModalTypes.AccountingDayRouting,
  ModalTypes.AllocatePenScanLots,
  ModalTypes.BusinessLabel,
  ModalTypes.EditIntegrationBusiness,
  ModalTypes.BulkUpdateBusiness,
  ModalTypes.BulkUpdateSaleLot,
  ModalTypes.BulkUpdateClearingSaleSaleLot,
  ModalTypes.BulkUpdateScanLabels,
  ModalTypes.BuyerPaymentSummary,
  ModalTypes.EditBusiness,
  ModalTypes.BusinessPayments,
  ModalTypes.Consignment,
  ModalTypes.DefaultBuyerWayProperty,
  ModalTypes.EditMasterRuleSet,
  ModalTypes.EditMasterRule,
  ModalTypes.EditNomination,
  ModalTypes.EditPayment,
  ModalTypes.EditReceivalLot,
  ModalTypes.EditRule,
  ModalTypes.EditRuleSet,
  ModalTypes.Filter,
  ModalTypes.ForceTransfer,
  ModalTypes.ImportWeighLots,
  ModalTypes.InterestPreview,
  ModalTypes.MoveScansToSale,
  ModalTypes.SideLoadedDataSets,
  ModalTypes.MoveToExistingRuleSet,
  ModalTypes.RetainProceeds,
  ModalTypes.RuleLibrary,
  ModalTypes.WeightSettings,
];

type HashState = { [key in ModalTypes]?: boolean };

/**
 * Convenience function to process a given hash portion of a URL to convert it into an object representation.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- queryString.parse returns dynamic shape
function getHash(locationHash: string): Record<string, any> {
  return queryString.parse(locationHash || "", {
    parseBooleans: true,
    parseNumbers: true,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- queryString.parse returns dynamic shape
  }) as Record<string, any>;
}

/**
 * works on the result of getHash to generate a simple true/false lookup of which modals are indicated as 'open' in the hash.
 *
 * @param hash
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- hash from queryString.parse
function deriveHashState(hash: Record<string, any>): HashState {
  return ManagedModalTypes.reduce((acc: HashState, modalType: ModalTypes) => {
    acc[modalType] = Boolean(hash[modalType]);
    return acc;
  }, {} as HashState);
}

/**
 *  Extracts and parses the parameters for a specific modal type from the URL hash.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- hash from queryString.parse
function getHashParams(hash: Record<string, any>, modalType: ModalTypes) {
  const raw = hash[modalType];
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

// Stores the last processed hash.
// The event listener is called when the url changes, but this system is only concerned with changes to the hash.
let lastHash: string | null = null;

/**
 * Listens to changes in the URL, specifically waiting for changes in the hash portion. Debouncing in the process.
 * Once a hash change has been detected, compares the state 'open' of each modal to what the URL indicates.
 * If a discrepancy is found, dispatches actions to either open or close the modal to bring the application state
 * in line with the URL.
 * This is primarily used for deep linking, backwards and forwards navigation, backwards compatibility with previous imnplementation.
 */
function reconcileStateOnHashChange(
  location: { hash: string },
  _ignored: string,
) {
  if (location.hash === lastHash) return;

  const newHash = getHash(location.hash);
  const newHashState = deriveHashState(newHash);

  const store = getStore();
  const state = store.getState();

  for (const modalType of ManagedModalTypes) {
    const isOpen: boolean = newHashState[modalType];
    const modalState = getContextByModalType(modalType)(state);
    if (isOpen) {
      const newContext = getHashParams(newHash, modalType);
      if (modalState?.isOpen !== true) {
        newContext.isOpen = true;
      }
      store.dispatch(updateModalContext(modalType, newContext));
    } else if (!isOpen && modalState?.isOpen) {
      store.dispatch(closeDispatchingModal(modalType, false));
    }
  }

  lastHash = location.hash;
}

/**
 * Sets up the modal manager to listen for URL hash changes so it may reconcile the redux state against the URL Hash.
 */
export function addModalManager() {
  appHistory.listen(reconcileStateOnHashChange);
  // Fire once to sync initial state
  reconcileStateOnHashChange(window.location, "REPLACE");
}
